import React, { useCallback, useEffect, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { Tab, Tabs } from "@mui/material";
import { useQuery } from "@tanstack/react-query";

// Utils & Mapping
import { calculateTotalNumberOfMatchRequest } from "../utils/bookingDetailsContainer";
import {
    convertProductCriteriaDataToPropsData,
    convertProductCriteriaMatchingInfoToPropsData,
    convertToGeneralCriteriaPropsData,
    convertBookingPotentialsToGeneralCriteriaPotentialPropsData,
    convertBookingPotentialsToPricePotentialPropsData,
    convertBookingPotentialsToZipCodePotentialPropsData
} from "../utils/bookingDetailsContainer/mapper";

// Komponenten
import { CriteriaGroup } from "../../criteria";
import { CustomTabPanel } from "../../../components/ui/tabs";
import BookingPriceOverview from "./BookingPriceOverview";
import BookingStickyHeader from "./BookingStickyHeader";
import BookingZipCodeOverview from "./BookingZipCodeOverview";
import BookingChangesHint from "./BookingChangesHint";
import NonPartnerBookingPromotion from "features/nonPartnerBookingPromotion/components/nonPartnerBookingPromotion";

// Hooks & Context
import useBookingTabRoute from "../hooks/bookingDetailsContainer/useBookingTabRoute";
import useBookingSticky from "../hooks/bookingDetailsContainer/useBookingSticky";
import { useSupplierInfo } from "layout/context/SupplierInfoContext";
import { RouteNames } from "layout/utils/SupplierContainer/routes";

// Assets
import "../../../assets/styles/booking/BookingDetailsContainer.scss";
import "../../../assets/styles/nonPartnerBookingPromotion/nonPartnerBookingPromotion.scss";

import mockDataJson from "./mockData.json";
import mockDataJson2 from "./mockData2.json";
import mockDataJson3 from "./mockData24Std.json";
import mockDataJson4 from "./mockDataInkontinenz.json";
import mockDataJson5 from "./mockDataPflegedienst.json";
import mockDataJson6 from "./mockDataPflegehilfsmittel.json";
import mockDataJson7 from "./mockDataStdBetreuung.json";
import mockDataJson8 from "./mockDataSitzlift.json";

// Typdefinitionen
import { IBookingDetail } from "../services/dtos";
import { BookingDetailsContainerTab } from "../types/enums";

export interface IProductPotentialState {
    generalCriteriaPotentialPercentage: number;
    productCriteriaPotentialPercentage: number;
    weekdayPotentialPercentage: number;
    zipCodePotentialPercentage: number;
    totalNumberOfSoldRequest: number;
    pricePotentialPercentage: number;
}


export const useGetBookingInfo = (selectedBookingId?: string) => {
    const isMock = true; // Für die Produktion auf false setzen
    return useQuery(
        ["getBookingPotentialsInfo", selectedBookingId],
        async () => {
            if (isMock) {
                // Simuliere eine Netzwerkverzögerung
                return new Promise<IBookingDetail>((resolve) => {
                    setTimeout(() => {
                        if (selectedBookingId === "1") {
                            resolve(mockDataJson);
                        } else if (selectedBookingId === "2") {
                            resolve(mockDataJson2);
                        } else if (selectedBookingId === "3") {
                            resolve(mockDataJson5);
                        } else if (selectedBookingId === "4") {
                            resolve(mockDataJson8);
                        } else if (selectedBookingId === "5") {
                            resolve(mockDataJson7);
                        }
                        else if (selectedBookingId === "6") {
                            resolve(mockDataJson4);
                        }
                        else if (selectedBookingId === "7") {
                            resolve(mockDataJson7);
                        }
                        else if (selectedBookingId === "8") {
                            resolve(mockDataJson8);
                        } else {
                            // Fallback, falls andere bookingId vorhanden ist
                            resolve(mockDataJson);
                        }
                    }, 500);
                });
            }
            // Hier könnte ein echter API-Call erfolgen, z.B.
            // return await get<IBookingDetail>(`/bookings/${bookingId}/potentialsInfo`);
        },
        { enabled: !!selectedBookingId }
    );
};


// Hauptkomponente BookingDetailsContainer
function BookingDetailsContainer() {
    const { bookingId } = useParams<{ bookingId: string }>();
    const { isSticky } = useBookingSticky();
    const { currentBookingTab, tabHandleChange } = useBookingTabRoute();
    const [soldRequest, setSoldRequest] = useState<any>(); // Typisierung ggf. anpassen
    const [selectedBookingId, setSelectedBookingId] = useState<string | undefined>(undefined);
    const { bookingList } = useSupplierInfo();
    const currentRoute = useLocation();

    const {
        data: initializeBookingInfo,
        isSuccess,
        isError
    } = useGetBookingInfo(selectedBookingId);

    useEffect(() => {
        if (
            bookingId &&
            currentRoute.pathname.match(RouteNames.WAITING_LIST_DETAILS) == null &&
            currentRoute.pathname.match(RouteNames.CAPACITY_LEAD_DETAILS) == null
        ) {
            // Im Standardfall aus der URL verwenden (außer bei waiting list oder capacity lead details)
            setSelectedBookingId(bookingId);
        } else {
            if (bookingList !== undefined) {
                if (bookingList.length > 0 && bookingList[0].bookingId) {
                    setSelectedBookingId(bookingList[0].bookingId);
                } else {
                    setSelectedBookingId("");
                }
            }
        }
    }, [bookingList, bookingId, currentRoute.pathname]);

    useEffect(() => {
        if (isSuccess && initializeBookingInfo) {
            intializeSoldRequestData(initializeBookingInfo);
        } else {
            setSoldRequest(undefined);
        }
    }, [isSuccess, initializeBookingInfo, selectedBookingId, isError]);

    function intializeSoldRequestData(data: IBookingDetail) {
        const {
            totalNumberOfSoldRequestLeadsFromGivenTimeFrame,
            productCriteriaPotentialPercentage,
            generalCriteriaPotential,
            pricePotentialPercentage,
            zipCodePotentialPercentage,
            weekdayPotentialPercentage
        } = data.preCalculcatedBookingPotentials;

        setSoldRequest({
            totalNumberOfSoldRequest: totalNumberOfSoldRequestLeadsFromGivenTimeFrame,
            generalCriteriaPotentialPercentage: generalCriteriaPotential,
            pricePotentialPercentage,
            zipCodePotentialPercentage,
            weekdayPotentialPercentage,
            productCriteriaPotentialPercentage
        });
    }

    function setPotentialNumberOnProductCriteriaValueChange(count: number) {
        setSoldRequest((prevData: any) => {
            if (!prevData) return undefined;
            return {
                ...prevData,
                productCriteriaPotentialPercentage:
                    prevData.totalNumberOfSoldRequest !== 0
                        ? count / prevData.totalNumberOfSoldRequest
                        : 0
            };
        });
    }

    function setPotentialNumberOnGeneralCriteriaValueChange(
        generalPotential: number,
        weekdayPotential: number
    ) {
        setSoldRequest((prevData: any) => {
            if (!prevData) return undefined;
            return {
                ...prevData,
                generalCriteriaPotentialPercentage: generalPotential,
                weekdayPotentialPercentage: weekdayPotential
            };
        });
    }

    function setPotentialNumberOnBudgetPriceValueChange(pricePotential: number) {
        setSoldRequest((prevData: any) => {
            if (!prevData) return undefined;
            return {
                ...prevData,
                pricePotentialPercentage: pricePotential
            };
        });
    }

    function handleBookingDropdownChange(booking: string) {
        console.log("Selected booking ID:", booking);
        setSelectedBookingId(booking);
    }

    const setPotentialNumberOnZipCodeSelectionChange = useCallback(
        (pricePotential: number) => {
            setSoldRequest((prevData: any) => {
                if (!prevData) return undefined;
                return {
                    ...prevData,
                    zipCodePotentialPercentage: pricePotential
                };
            });
        },
        []
    );

    return (
        <>
            {initializeBookingInfo && selectedBookingId !== undefined ? (
                <>
                    <BookingStickyHeader
                        totalNumberOfRequest={soldRequest?.totalNumberOfSoldRequest}
                        matchNumberOfRequest={
                            soldRequest ? calculateTotalNumberOfMatchRequest(soldRequest) : undefined
                        }
                        bookingList={bookingList}
                        selectedBookingId={selectedBookingId}
                        isSticky={isSticky}
                        onBookingDropdownChange={handleBookingDropdownChange}
                        budgetInfo={initializeBookingInfo.bookingInfo.budget}
                        cancellationQuote={initializeBookingInfo.bookingInfo.cancellationQuote}
                    />
                    {isSticky && <div className="sticky-placeholder"></div>}
                    <Tabs
                        className="container"
                        value={currentBookingTab}
                        onChange={tabHandleChange}
                        aria-label="booking tabs"
                    >
                        <Tab label="Vorqualifizierung" value={BookingDetailsContainerTab.CRITERIA} />
                        <Tab label="Budgetoptimierung" value={BookingDetailsContainerTab.BudgetOptimization} />
                        <Tab label="Zielgebiet" value={BookingDetailsContainerTab.ZipCode} />
                    </Tabs>
                    <CustomTabPanel className="container" value={currentBookingTab} index={BookingDetailsContainerTab.CRITERIA}>
                        <h2>Prüfen Sie den Einfluss Ihrer Kriterien auf Ihren Marktanteil</h2>
                        <BookingChangesHint />
                        <CriteriaGroup
                            bookingId={selectedBookingId}
                            productCriteriaGroups={convertProductCriteriaDataToPropsData(
                                initializeBookingInfo.productCriteriaInformations
                            )}
                            calculatedCriteriaMatching={convertProductCriteriaMatchingInfoToPropsData(
                                initializeBookingInfo.productCriteriaMatchingInformations
                            )}
                            generalCriteria={convertToGeneralCriteriaPropsData(initializeBookingInfo.bookingInfo)}
                            generalCriteriaPotentialInfos={convertBookingPotentialsToGeneralCriteriaPotentialPropsData(
                                initializeBookingInfo.bookingPotentialMatchingInfomations
                            )}
                            getPotentialNumberOnProductCriteriaValueChange={setPotentialNumberOnProductCriteriaValueChange}
                            getPotentialNumberOnGeneralCriteriaValueChange={setPotentialNumberOnGeneralCriteriaValueChange}
                        />
                    </CustomTabPanel>
                    <CustomTabPanel className="container" value={currentBookingTab} index={BookingDetailsContainerTab.BudgetOptimization}>
                        <BookingPriceOverview
                            cancellationQuote={initializeBookingInfo.bookingInfo.cancellationQuote}
                            effectiveLeadNettoPriceAdjustmentState={initializeBookingInfo.bookingInfo.effectiveLeadNettoPriceAdjustmentState}
                            getPotentialNumberOnBookingPriceValueChange={setPotentialNumberOnBudgetPriceValueChange}
                            bookingPricePotentialInfos={convertBookingPotentialsToPricePotentialPropsData(
                                initializeBookingInfo.bookingPotentialMatchingInfomations
                            )}
                            bookingId={selectedBookingId}
                            leadPrice1={initializeBookingInfo.bookingInfo.leadPrice1}
                            leadPrice2={initializeBookingInfo.bookingInfo.leadPrice2}
                            leadPrice3={initializeBookingInfo.bookingInfo.leadPrice3}
                            leadType={initializeBookingInfo.bookingInfo.leadType}
                        />
                    </CustomTabPanel>
                    <CustomTabPanel className="container" value={currentBookingTab} index={BookingDetailsContainerTab.ZipCode}>
                        <BookingZipCodeOverview
                            bookingZipCodePotentialInfos={convertBookingPotentialsToZipCodePotentialPropsData(
                                initializeBookingInfo.bookingPotentialMatchingInfomations
                            )}
                            bookingId={selectedBookingId}
                            bookingAreaInformations={initializeBookingInfo.bookingAreaInformations}
                            getPotentialNumberOnBookingZipCodeSelectionChange={setPotentialNumberOnZipCodeSelectionChange}
                        />
                    </CustomTabPanel>
                </>
            ) : (
                selectedBookingId === "" && <NonPartnerBookingPromotion />
            )}
        </>
    );
}

export default BookingDetailsContainer;
